import React, { useMemo } from 'react';
import { DashboardAlerte } from '../types';
import {
  ExclamationTriangleIcon,
  BellAlertIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

interface DashboardSidebarUrgencesProps {
  alertes: DashboardAlerte[];
  onOuvrirProjet: (projetId: string) => void;
}

interface ProjetUrgence {
  projetId: string;
  projetNom: string;
  niveau: 'rouge' | 'orange';
  alertes: DashboardAlerte[];
}

const DashboardSidebarUrgences: React.FC<DashboardSidebarUrgencesProps> = ({
  alertes,
  onOuvrirProjet,
}) => {
  const projetsUrgents = useMemo<ProjetUrgence[]>(() => {
    const map = new Map<string, ProjetUrgence>();

    for (const alerte of alertes) {
      const existing = map.get(alerte.projetId);
      if (existing) {
        existing.alertes.push(alerte);
        if (alerte.niveau === 'rouge') existing.niveau = 'rouge';
      } else {
        map.set(alerte.projetId, {
          projetId: alerte.projetId,
          projetNom: alerte.projetNom,
          niveau: alerte.niveau,
          alertes: [alerte],
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.niveau !== b.niveau) return a.niveau === 'rouge' ? -1 : 1;
      return b.alertes.length - a.alertes.length;
    });
  }, [alertes]);

  if (alertes.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 h-full flex flex-col">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center">
            <BellAlertIcon className="w-4 h-4 text-violet-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
            Urgences
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 text-center">
          <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
            <span className="text-emerald-500 text-xl font-bold">&#10003;</span>
          </div>
          <p className="text-sm font-medium text-emerald-600">Aucune alerte</p>
          <p className="text-[11px] text-gray-400 mt-1">Tout est sous contrôle</p>
        </div>
      </div>
    );
  }

  const nbProjetsRouges = projetsUrgents.filter(p => p.niveau === 'rouge').length;
  const nbProjetsOranges = projetsUrgents.filter(p => p.niveau === 'orange').length;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center">
            <BellAlertIcon className="w-4 h-4 text-violet-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
            Urgences
          </h3>
        </div>
        <div className="flex items-center gap-1.5">
          {nbProjetsRouges > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-600">
              {nbProjetsRouges} critique{nbProjetsRouges > 1 ? 's' : ''}
            </span>
          )}
          {nbProjetsOranges > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-600">
              {nbProjetsOranges} vigilance
            </span>
          )}
        </div>
      </div>

      {/* Liste des projets urgents (scrollable, remplit l'espace restant) */}
      <div className="space-y-2 flex-1 overflow-y-auto pr-1">
        {projetsUrgents.map((projet) => {
          const isRouge = projet.niveau === 'rouge';
          const alertesVisibles = projet.alertes.slice(0, 2);
          const alertesMasquees = projet.alertes.length - alertesVisibles.length;

          return (
            <div
              key={projet.projetId}
              onClick={() => onOuvrirProjet(projet.projetId)}
              className="relative p-2.5 rounded-lg cursor-pointer transition-all group border border-gray-100 bg-gray-50/50 hover:bg-violet-50 hover:border-violet-200"
            >
              {/* Nom du projet + badges + chevron */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold text-gray-700 truncate">
                    {projet.projetNom}
                  </span>
                  <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                    isRouge ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
                  }`}>
                    {projet.alertes.length}
                  </span>
                </div>
                <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-violet-500 flex-shrink-0 ml-2" />
              </div>

              {/* Alertes condensées */}
              <div className="space-y-0.5">
                {alertesVisibles.map((alerte) => (
                  <div key={alerte.id} className="flex items-center gap-1.5">
                    <ExclamationTriangleIcon className={`w-3 h-3 flex-shrink-0 ${
                      alerte.niveau === 'rouge' ? 'text-red-400' : 'text-amber-400'
                    }`} />
                    <p className={`text-[11px] leading-tight truncate ${
                      alerte.niveau === 'rouge' ? 'text-red-600' : 'text-amber-700'
                    }`}>
                      {alerte.probleme}
                    </p>
                    {alerte.source === 'incident' && (
                      <span className="inline-flex px-1 rounded text-[9px] font-semibold bg-violet-100 text-violet-600 flex-shrink-0">
                        Inc.
                      </span>
                    )}
                    {alerte.source === 'derive_tendance' && (
                      <span className="inline-flex px-1 rounded text-[9px] font-semibold bg-blue-100 text-blue-600 flex-shrink-0">
                        Tend.
                      </span>
                    )}
                  </div>
                ))}
                {alertesMasquees > 0 && (
                  <p className="text-[10px] text-gray-400 pl-4.5">
                    +{alertesMasquees} autre{alertesMasquees > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardSidebarUrgences;
